"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { AlanaLogo } from "@/components/ui/AlanaLogo";

/** Copy variations — randomly picked on each mount */
const COPY_VARIANTS = [
  {
    headline: "this page abandoned its cart",
    subtext: [
      "we checked every aisle and even asked our ai.",
      "she\u2019s just as confused as you are.",
    ],
  },
  {
    headline: "out of stock forever",
    subtext: [
      "this page had one unit left and someone else got it.",
      "just kidding. it never existed.",
    ],
  },
  {
    headline: "wrong delivery address",
    subtext: [
      "this page was supposed to arrive but the url got lost in transit.",
      "tracking says: \u201cdelivered to the void.\u201d",
    ],
  },
  {
    headline: "this shelf is empty",
    subtext: [
      "someone forgot to restock this page.",
      "our warehouse team has been notified. (not really)",
    ],
  },
  {
    headline: "coupon expired",
    subtext: [
      "this page\u2019s link expired faster than a flash sale.",
      "some deals just aren\u2019t meant to last.",
    ],
  },
  {
    headline: "lost in the mall",
    subtext: [
      "you took a wrong turn somewhere between checkout and here.",
      "it happens to the best of us.",
    ],
  },
  {
    headline: "item removed from wishlist",
    subtext: [
      "looks like the internet removed this page from its wishlist.",
      "harsh, but fair.",
    ],
  },
  {
    headline: "no reviews yet",
    subtext: [
      "this page has zero reviews because it doesn\u2019t exist.",
      "hard to rate something that was never in stock.",
    ],
  },
];

const ALANA_COLORS = [
  "#ff8086", // coral
  "#7de4ff", // sky
  "#ffe181", // yellow
  "#40e3c6", // mint
  "#948bff", // lavender
];

/** Floating dot particle */
function Particle({ delay, color }: { delay: number; color: string }) {
  const startX = Math.random() * 100;
  const startY = Math.random() * 100;
  const size = Math.random() * 4 + 2;
  const duration = Math.random() * 8 + 12;

  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        left: `${startX}%`,
        top: `${startY}%`,
        background: color,
      }}
      animate={{
        y: [0, -30, 20, -15, 0],
        x: [0, 20, -15, 10, 0],
        opacity: [0, 0.8, 0.4, 0.7, 0],
        scale: [0, 1, 0.6, 1.2, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

/** Floating mini shopping bag */
function FloatingBag({
  delay,
  color,
  x,
  y,
}: {
  delay: number;
  color: string;
  x: number;
  y: number;
}) {
  const duration = 14 + Math.random() * 8;
  return (
    <motion.div
      className="absolute"
      style={{ left: `${x}%`, top: `${y}%` }}
      animate={{
        y: [0, -25, 15, -10, 0],
        x: [0, 12, -8, 6, 0],
        rotate: [0, 12, -8, 6, 0],
        opacity: [0, 0.5, 0.25, 0.4, 0],
      }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg width="36" height="44" viewBox="0 0 18 22" fill="none">
        <path d="M2 7h14l-1.5 13H3.5L2 7z" fill={color} opacity="0.35" />
        <path
          d="M6 7V5a3 3 0 016 0v2"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.5"
        />
      </svg>
    </motion.div>
  );
}

/** Floating price tag */
function FloatingTag({
  delay,
  color,
  x,
  y,
}: {
  delay: number;
  color: string;
  x: number;
  y: number;
}) {
  const duration = 12 + Math.random() * 8;
  return (
    <motion.div
      className="absolute"
      style={{ left: `${x}%`, top: `${y}%` }}
      animate={{
        y: [0, -18, 10, -12, 0],
        x: [0, -10, 8, -5, 0],
        rotate: [0, -15, 10, -8, 0],
        opacity: [0, 0.45, 0.2, 0.35, 0],
      }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg width="32" height="40" viewBox="0 0 16 20" fill="none">
        <path
          d="M1 3a2 2 0 012-2h6l6 6v10a2 2 0 01-2 2H3a2 2 0 01-2-2V3z"
          fill={color}
          opacity="0.3"
        />
        <circle cx="11" cy="5" r="1.2" fill="white" opacity="0.4" />
      </svg>
    </motion.div>
  );
}

/** Shopping-themed floating elements data */
const FLOATING_ELEMENTS: Array<{
  type: "bag" | "tag";
  x: number;
  y: number;
  delay: number;
  colorIndex: number;
}> = [
  { type: "bag", x: 8, y: 15, delay: 0, colorIndex: 0 },
  { type: "tag", x: 85, y: 20, delay: 1.5, colorIndex: 1 },
  { type: "bag", x: 75, y: 70, delay: 3, colorIndex: 2 },
  { type: "tag", x: 15, y: 75, delay: 4.5, colorIndex: 3 },
  { type: "bag", x: 90, y: 45, delay: 6, colorIndex: 4 },
  { type: "tag", x: 5, y: 45, delay: 7.5, colorIndex: 0 },
  { type: "bag", x: 60, y: 10, delay: 9, colorIndex: 1 },
  { type: "tag", x: 35, y: 85, delay: 10.5, colorIndex: 2 },
];

export default function NotFound() {
  const t = useTranslations("standalone.notFound");
  const copy = useMemo(
    () => COPY_VARIANTS[Math.floor(Math.random() * COPY_VARIANTS.length)],
    [],
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Dark background */}
      <div className="absolute inset-0" style={{ background: "#0a0a0a" }} />

      {/* Dot grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          opacity: 0.15,
        }}
      />

      {/* Aurora blobs */}
      <motion.div
        className="absolute w-[700px] h-[700px] rounded-full blur-[180px]"
        style={{ bottom: "-200px", left: "-150px" }}
        animate={{
          opacity: [0.5, 0.8, 0.6, 0.9, 0.5],
          x: [0, 100, 40, -50, 0],
          y: [0, -80, 50, -40, 0],
          scale: [1, 1.2, 0.9, 1.15, 1],
          background: [
            "rgba(255, 128, 134, 0.4)",
            "rgba(148, 139, 255, 0.35)",
            "rgba(255, 128, 134, 0.45)",
            "rgba(64, 227, 198, 0.3)",
            "rgba(255, 128, 134, 0.4)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full blur-[150px]"
        style={{ top: "-150px", right: "-100px" }}
        animate={{
          opacity: [0.3, 0.6, 0.4, 0.7, 0.3],
          x: [0, -80, 40, -60, 0],
          y: [0, 80, -30, 60, 0],
          background: [
            "rgba(148, 139, 255, 0.3)",
            "rgba(125, 228, 255, 0.3)",
            "rgba(255, 225, 129, 0.25)",
            "rgba(148, 139, 255, 0.35)",
            "rgba(148, 139, 255, 0.3)",
          ],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full blur-[120px]"
        style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
        animate={{
          opacity: [0.2, 0.4, 0.25, 0.45, 0.2],
          scale: [1, 1.3, 0.8, 1.2, 1],
          background: [
            "rgba(64, 227, 198, 0.2)",
            "rgba(255, 128, 134, 0.25)",
            "rgba(148, 139, 255, 0.2)",
            "rgba(125, 228, 255, 0.25)",
            "rgba(64, 227, 198, 0.2)",
          ],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating particles (dots) */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <Particle
            key={`p-${i}`}
            delay={i * 0.8}
            color={ALANA_COLORS[i % ALANA_COLORS.length]}
          />
        ))}
      </div>

      {/* Floating shopping elements (bags + tags) */}
      <div className="absolute inset-0 pointer-events-none">
        {FLOATING_ELEMENTS.map((el, i) =>
          el.type === "bag" ? (
            <FloatingBag
              key={`bag-${i}`}
              delay={el.delay}
              color={ALANA_COLORS[el.colorIndex]}
              x={el.x}
              y={el.y}
            />
          ) : (
            <FloatingTag
              key={`tag-${i}`}
              delay={el.delay}
              color={ALANA_COLORS[el.colorIndex]}
              x={el.x}
              y={el.y}
            />
          ),
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center">
        {/* Hero shopping bag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: [0, -6, 0],
            rotate: [-2, 2, -2],
          }}
          transition={{
            opacity: { duration: 0.6 },
            y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
            rotate: { duration: 6, repeat: Infinity, ease: "easeInOut" },
          }}
          className="mb-2"
        >
          <AlanaLogo
            variant="icon"
            className="w-28 h-28 sm:w-36 sm:h-36 drop-shadow-[0_0_40px_rgba(255,128,134,0.3)]"
          />
        </motion.div>

        {/* 404 */}
        <motion.h1
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-7xl sm:text-8xl lg:text-9xl font-black leading-none text-white tracking-tight"
          style={{
            fontFamily: "MuseoModerno, sans-serif",
            textShadow:
              "0 0 40px rgba(255, 128, 134, 0.25), 0 0 80px rgba(148, 139, 255, 0.1)",
          }}
        >
          404
        </motion.h1>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-5 space-y-3"
        >
          <h2
            className="text-xl sm:text-2xl font-semibold text-white"
            style={{ fontFamily: "MuseoModerno, sans-serif" }}
          >
            {copy.headline}
          </h2>
          <p className="text-white/50 text-sm max-w-sm leading-relaxed">
            {copy.subtext[0]}
            <br />
            {copy.subtext[1]}
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row gap-3"
        >
          <Link
            href="/"
            className="px-6 py-3 rounded-xl text-sm font-medium text-white lowercase transition-all hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #ff8086, #948bff)",
              boxShadow: "0 4px 20px rgba(255, 128, 134, 0.3)",
              fontFamily: "MuseoModerno, sans-serif",
            }}
          >
            {t("backToShopping")}
          </Link>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.p
        className="absolute bottom-8 text-white/20 text-xs lowercase"
        style={{ fontFamily: "MuseoModerno, sans-serif" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        {t("footer")}
      </motion.p>
    </div>
  );
}
